import { useMemo, useState } from "react";
import FormWrapper from "../FormWrapper/FormWrapper";
import "./SenderForm.css";
import { TextField, MenuItem, Typography, Autocomplete } from "@mui/material";
import {
  validatePostCode,
  validateAndFormatPostCode,
  validateAndFormatPostCodeFromWeb,
} from "../../../../utils/postCodeValidation";
import { validateEmail } from "../../../../utils/contactValidation";
import { toast } from "react-toastify";

const SenderForm = ({
  senderName,
  senderEmail,
  senderMobileNumber,
  senderCountry,
  senderLandmark,
  senderCity,
  senderPostCode,
  senderDoorNo,
  senderStreetName,
  senderRemarks,
  senderAddress,
  updateFields,
  fetchedFormData,
  emailRequired = true,
}) => {
  const { country, city } = fetchedFormData;
  const [senderEmailError, setSenderEmailError] = useState("");

  const filteredCities = useMemo(() => {
    if (!senderCountry || !city) return [];

    // Find cities that match the selected country's ID
    // Note: We use String() comparison to be safe against number/string mismatches
    return city.filter(
      (item) => String(item.countryId) === String(senderCountry),
    );
  }, [senderCountry, city]);

  const textFieldStyle = {
    backgroundColor: "hsl(var(--form-input-bg))",
    borderRadius: "12px",
    width: "100%",
    /* Hide MUI floating label (user wants normal inputs without label in/above the box) */
    "& .MuiInputLabel-root": { display: "none" },
    "& input::placeholder": {
      color: "hsl(var(--form-placeholder))",
      opacity: 1,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "hsl(var(--form-input-border))",
        borderRadius: "12px",
      },
      "&:hover fieldset": {
        borderColor: "hsl(var(--form-input-border-focus))",
      },
      "&.Mui-focused fieldset": {
        borderColor: "hsl(var(--form-input-border-focus))",
      },
    },
  };

  return (
    <FormWrapper title="Sender Details">
      <Typography
        variant="h5"
        className="text-center"
        sx={{
          fontWeight: 700,
          marginBottom: "8px",
          color: "hsl(var(--foreground))",
          textAlign: "center",
        }}
      >
        Sender Information Form
      </Typography>
      <Typography
        variant="body2"
        className="text-center"
        sx={{ color: "hsl(var(--muted-foreground))", marginBottom: "16px" }}
      >
        Please fill in your sender information below.
      </Typography>
      <div className="flex flex-col gap-6 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Sender Name</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required
              id="sender-name"
              placeholder="Full name"
              variant="outlined"
              value={senderName}
              onChange={(e) => updateFields({ senderName: e.target.value })}
            />
          </div>

          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">
              {emailRequired ? "Email" : "Email (optional)"}
            </div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required={emailRequired}
              id="email"
              type="email"
              placeholder={emailRequired ? "Email address" : "Email address (optional)"}
              variant="outlined"
              value={senderEmail}
              onChange={(e) => {
                updateFields({ senderEmail: e.target.value });
                if (senderEmailError) setSenderEmailError("");
              }}
              onBlur={() => {
                const result = validateEmail(senderEmail, { required: emailRequired });
                setSenderEmailError(result.valid ? "" : result.error);
              }}
              error={!!senderEmailError}
              helperText={senderEmailError}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Mobile Number</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              required
              id="mobile-number"
              placeholder="Mobile number"
              variant="outlined"
              value={senderMobileNumber}
              onChange={(e) => {
                const newValue = e.target.value;
                // Allow only empty string or numeric characters
                if (/^\d*$/.test(newValue)) {
                  updateFields({ senderMobileNumber: newValue });
                }
              }}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Country</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              select
              required
              id="country"
              variant="outlined"
              value={senderCountry}
              onChange={(e) => {
                updateFields({
                  senderCountry: e.target.value,
                  senderCity: "",
                });
              }}
            >
              {/* Disabled Placeholder Item */}
              <MenuItem
                value=""
                disabled
                sx={{ color: "text.disabled", fontStyle: "italic" }} // Makes it look like a placeholder
              >
                Select Country
              </MenuItem>

              {/* Existing Country List */}
              {country?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        {/* flex w-full gap-6 */}
        <div className="flex flex-col md:flex-row w-full gap-6">
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">City</div>
            <Autocomplete
              id="city"
              options={filteredCities}
              getOptionLabel={(option) => option.name || ""}
              value={
                filteredCities.find(
                  (item) => String(item.id) === String(senderCity),
                ) || null
              }
              onChange={(e, newValue) => {
                updateFields({ senderCity: newValue ? newValue.id : "" });
              }}
              isOptionEqualToValue={(option, value) =>
                String(option.id) === String(value.id)
              }
              // 1. Disable the Autocomplete logic if no country is selected
              disabled={!senderCountry}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={textFieldStyle}
                  fullWidth
                  required
                  placeholder="Select City"
                  variant="outlined"
                  // 2. Pass disabled to the TextField to ensure visual consistency
                  disabled={!senderCountry}
                />
              )}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">
              Nearest Landmark (Optional)
            </div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id="landmark"
              placeholder="Landmark"
              variant="outlined"
              value={senderLandmark}
              onChange={(e) => updateFields({ senderLandmark: e.target.value })}
            />
          </div>
        </div>

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">Post Code</div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            required
            id="sender-post-code"
            placeholder="Post code"
            variant="outlined"
            value={senderPostCode}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              updateFields({ senderPostCode: value });
            }}
            onBlur={async (e) => {
              const value = e.target.value.trim();
              if (value && senderCountry) {
                try {
                  // Use web API validation
                  const validation = await validateAndFormatPostCodeFromWeb(
                    value,
                    senderCountry,
                    country || [],
                  );
                  if (!validation.isValid) {
                    toast.error(validation.error, {
                      position: "top-right",
                      autoClose: 3000,
                    });
                  } else {
                    // Format the post code if valid
                    if (validation.formatted !== value) {
                      updateFields({ senderPostCode: validation.formatted });
                    }
                    // Show success message if API validation succeeded
                    if (validation.data && !validation.fallback) {
                      toast.success("Post code validated successfully", {
                        position: "top-right",
                        autoClose: 2000,
                      });
                    }
                  }
                } catch (error) {
                  console.error("Post code validation error:", error);
                  // Fallback to format validation
                  const formatValidation = validatePostCode(
                    value,
                    senderCountry,
                    country || [],
                  );
                  if (!formatValidation.isValid) {
                    toast.error(formatValidation.error, {
                      position: "top-right",
                      autoClose: 3000,
                    });
                  }
                }
              }
            }}
            helperText={
              senderCountry && senderPostCode
                ? (() => {
                    const validation = validatePostCode(
                      senderPostCode,
                      senderCountry,
                      country || [],
                    );
                    return validation.isValid ? "" : validation.error;
                  })()
                : ""
            }
            error={
              senderCountry && senderPostCode
                ? (() => {
                    return !validatePostCode(
                      senderPostCode,
                      senderCountry,
                      country || [],
                    ).isValid;
                  })()
                : false
            }
          />
        </div>

        <div className='flex flex-col md:flex-row w-full gap-6'>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Door No</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id='sender-door-no'
              placeholder='Door / flat / unit number'
              variant='outlined'
              value={senderDoorNo}
              onChange={(e) => updateFields({ senderDoorNo: e.target.value })}
            />
          </div>
          <div className="w-full space-y-2">
            <div className="booking-form-label text-sm">Street Name</div>
            <TextField
              sx={textFieldStyle}
              fullWidth
              id='sender-street-name'
              placeholder='Street name'
              variant='outlined'
              value={senderStreetName}
              onChange={(e) => updateFields({ senderStreetName: e.target.value })}
            />
          </div>
        </div>

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">Remarks (Optional)</div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            multiline
            rows={2}
            id='sender-remarks'
            placeholder='Any additional notes for delivery'
            variant='outlined'
            value={senderRemarks}
            onChange={(e) => updateFields({ senderRemarks: e.target.value })}
          />
        </div>

        <div className="w-full space-y-2">
          <div className="booking-form-label text-sm">
            Additional Address Information
          </div>
          <TextField
            sx={textFieldStyle}
            fullWidth
            multiline
            rows={4}
            required
            id="address"
            placeholder="Enter your full address details..."
            variant="outlined"
            value={senderAddress}
            onChange={(e) => updateFields({ senderAddress: e.target.value })}
          />
        </div>
      </div>
    </FormWrapper>
  );
};

export default SenderForm;
